import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import axios from 'axios';
import toast from 'react-hot-toast';
import { V_URL } from '../../../BaseUrl';
import { Pagination } from '../Table';
import { Calendar, Filter, FileX } from 'lucide-react';

const ProjectUsageSummary = () => {
    const [usageStartDate, setUsageStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [usageEndDate, setUsageEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [usageData, setUsageData] = useState([]);
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageSearch, setUsageSearch] = useState('');
    const [usagePage, setUsagePage] = useState(1);
    const usagePerPage = 10;
    const [isUsageFilterOpen, setIsUsageFilterOpen] = useState(false);
    const [downloadingMasterReport, setDownloadingMasterReport] = useState(false);

    const fetchProjectUsageSummary = async (startDate = usageStartDate, endDate = usageEndDate) => {
        try {
            setUsageLoading(true);
            const payload = {
                firm_id: localStorage.getItem("PAY_USER_FIRM_ID"),
                year_id: localStorage.getItem("PAY_USER_YEAR_ID"),
                start_date: startDate,
                end_date: endDate,
            };
            const res = await axios.post(`${V_URL}/prosafe/pro-project-usage-summary`, payload, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
                },
            });
            if (res?.data?.success) {
                setUsageData(res?.data?.data?.summary || []);
            } else {
                setUsageData([]);
            }
        } catch (error) {
            console.error("fetchProjectUsageSummary error:", error);
        } finally {
            setUsageLoading(false);
        }
    };

    const handleDownloadMasterReportExcel = async () => {
        try {
            setDownloadingMasterReport(true);
            const payload = {
                firm_id: localStorage.getItem("PAY_USER_FIRM_ID"),
                year_id: localStorage.getItem("PAY_USER_YEAR_ID"),
                start_date: usageStartDate || moment().startOf("month").format("YYYY-MM-DD"),
                end_date: usageEndDate || moment().format("YYYY-MM-DD"),
                project_ids: [],
            };

            const res = await axios.post(`${V_URL}/prosafe/pro-master-report-xlsx`, payload, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("PAY_USER_TOKEN"),
                },
            });

            if (res?.data?.success && res?.data?.data?.url) {
                window.open(res.data.data.url, "_blank");
                toast.success(res?.data?.message || "Master Report Excel downloaded successfully");
            } else {
                toast.error(res?.data?.message || "Failed to generate Master Report Excel");
            }
        } catch (err) {
            console.error("handleDownloadMasterReportExcel error:", err);
            toast.error(err?.response?.data?.message || "Error generating Master Report Excel");
        } finally {
            setDownloadingMasterReport(false);
        }
    };

    useEffect(() => {
        fetchProjectUsageSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filter and Pagination for Project Usage Summary Table
    const filteredUsage = useMemo(() => {
        let list = usageData;
        if (usageSearch && usageSearch.trim()) {
            const s = usageSearch.trim().toLowerCase();
            list = list.filter((u) => {
                const name = String(u.project_name || '').toLowerCase();
                const no = String(u.project_no || '').toLowerCase();
                return name.includes(s) || no.includes(s);
            });
        }
        return list;
    }, [usageData, usageSearch]);

    const paginatedUsage = useMemo(() => {
        const startIndex = (usagePage - 1) * usagePerPage;
        return filteredUsage.slice(startIndex, startIndex + usagePerPage);
    }, [filteredUsage, usagePage, usagePerPage]);

    const usageGrandTotals = useMemo(() => {
        let totalQty = 0;
        let totalAmt = 0;
        filteredUsage.forEach((u) => {
            totalQty += Number(u.total_use_qty || 0);
            totalAmt += Number(u.total_amount || 0);
        });
        return { totalQty, totalAmt };
    }, [filteredUsage]);

    return (
        <div className="row mb-4">
            <div className="col-sm-12">
                <div className="card card-table show-entire">
                    <div className="card-body">
                        {/* Header matching standard listing UI design */}
                        <div className="page-table-header mb-3">
                            <div className="row align-items-center">
                                <div className="col-12 col-md-7 col-lg-6 mb-2 mb-md-0">
                                    <div className="doctor-table-blk">
                                        <h3>Project-wise Material Usage Summary</h3>
                                        {/* Native Search Box */}
                                        <div className="doctor-search-blk">
                                            <div className="top-nav-search table-search-blk">
                                                <form onSubmit={(e) => e.preventDefault()}>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search here"
                                                        value={usageSearch}
                                                        onChange={(e) => {
                                                            setUsageSearch(e.target.value);
                                                            setUsagePage(1);
                                                        }}
                                                    />
                                                    <button className="btn" type="button">
                                                        <img src="/assets/img/icons/search-normal.svg" alt="Search" />
                                                    </button>
                                                </form>
                                            </div>
                                            <div className="add-group">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const s = moment().startOf('month').format('YYYY-MM-DD');
                                                        const e = moment().format('YYYY-MM-DD');
                                                        setUsageStartDate(s);
                                                        setUsageEndDate(e);
                                                        setUsageSearch('');
                                                        setUsagePage(1);
                                                        fetchProjectUsageSummary(s, e);
                                                    }}
                                                    className="btn btn-primary doctor-refresh ms-2"
                                                    title="Refresh"
                                                >
                                                    <img src="/assets/img/icons/re-fresh.svg" alt="refresh" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn btn-primary ms-2 ${isUsageFilterOpen ? 'active' : ''}`}
                                                    onClick={() => setIsUsageFilterOpen(!isUsageFilterOpen)}
                                                    data-bs-toggle="collapse"
                                                    data-bs-target="#usageFilterPanel"
                                                    aria-expanded={isUsageFilterOpen ? "true" : "false"}
                                                    aria-controls="usageFilterPanel"
                                                    data-toggle="tooltip"
                                                    data-placement="top"
                                                    title="Filter"
                                                >
                                                    <Filter size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-md-5 col-lg-6 text-md-end d-flex flex-wrap align-items-center justify-content-md-end gap-2">
                                    <button
                                        type="button"
                                        onClick={handleDownloadMasterReportExcel}
                                        style={{ height: "40px", width: "42px" }}
                                        className="btn btn-primary rounded-2 p-0 d-flex justify-content-center align-items-center"
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        title="Download Master Report Excel"
                                        disabled={downloadingMasterReport}
                                    >
                                        {downloadingMasterReport ? (
                                            <span className="spinner-border spinner-border-sm" role="status" />
                                        ) : (
                                            <FileX size={17} />
                                        )}
                                    </button>
                                    <Link
                                        to="/main-store/user/master-report"
                                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                        style={{ whiteSpace: 'nowrap', height: '40px', padding: '0 14px' }}
                                        title="Open Master Report Page"
                                    >
                                        <i className="feather-file-text me-1" /> Master Report
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Collapsible Filter Panel matching other listing UI */}
                        <div className={`collapse ${isUsageFilterOpen ? "show" : ""}`} id="usageFilterPanel">
                            <div className="mb-3 p-3">
                                <div className="row align-end g-3">
                                    <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                        <label className="form-label fs-13 fw-semibold text-muted mb-1">Start Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={usageStartDate}
                                            onChange={(e) => setUsageStartDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                        <label className="form-label fs-13 fw-semibold text-muted mb-1">To Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={usageEndDate}
                                            onChange={(e) => setUsageEndDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-12 col-sm-12 col-md-4 col-lg-4 d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-primary flex-grow-1 fw-semibold d-flex align-items-center justify-content-center gap-1"
                                            style={{ height: '42px' }}
                                            onClick={() => {
                                                setUsagePage(1);
                                                fetchProjectUsageSummary(usageStartDate, usageEndDate);
                                            }}
                                            disabled={usageLoading}
                                        >
                                            {usageLoading ? (
                                                <span className="spinner-border spinner-border-sm" role="status" />
                                            ) : (
                                                <Calendar size={15} />
                                            )}
                                            <span>Apply Filter</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Summary Table matching standard listing UI design */}
                        <div className="table-responsive">
                            <table className="table border-0 custom-table comman-table mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>Sr.</th>
                                        <th>PROJECT NAME</th>
                                        <th className="text-center" style={{ minWidth: '160px' }}>TOTAL USE QTY</th>
                                        <th className="text-end" style={{ minWidth: '180px' }}>TOTAL AMOUNT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usageLoading ? (
                                        <tr>
                                            <td colSpan="4" className="text-center py-4 text-muted">
                                                <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                                Loading project usage summary...
                                            </td>
                                        </tr>
                                    ) : paginatedUsage.length > 0 ? (
                                        paginatedUsage.map((u, idx) => {
                                            const cleanNo = (u.project_no || '').replace(/^[,\s]+|[,\s]+$/g, '').trim();
                                            let displayProject = u.project_name || 'Unassigned / General';
                                            if (cleanNo && u.project_name) {
                                                if (!u.project_name.toLowerCase().includes(`(${cleanNo.toLowerCase()})`)) {
                                                    displayProject = `${u.project_name} (${cleanNo})`;
                                                }
                                            }

                                            return (
                                                <tr key={u.project_id || idx}>
                                                    <td>{(usagePage - 1) * usagePerPage + idx + 1}</td>
                                                    <td>
                                                        <span className="fw-bold text-dark fs-14">
                                                            {displayProject}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="fw-bold fs-15 text-primary">
                                                            {Number(u.total_use_qty || 0).toLocaleString('en-IN', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            })}
                                                        </span>
                                                    </td>
                                                    <td className="text-end">
                                                        <span className="fw-bold fs-15 text-dark">
                                                            ₹ {Number(u.total_amount || 0).toLocaleString('en-IN', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            })}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center py-4 text-muted">
                                                <div className="no-table-data text-center py-2">
                                                    No Material Usage Recorded for the Selected Date Range!
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                {filteredUsage.length > 0 && (
                                    <tfoot style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                                        <tr className="fw-bold">
                                            <td colSpan="2" className="text-end text-uppercase fs-13 text-secondary">
                                                Grand Total:
                                            </td>
                                            <td className="text-center fs-15 text-danger fw-bold">
                                                {usageGrandTotals.totalQty.toLocaleString('en-IN', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="text-end fs-15 text-success fw-bold">
                                                ₹ {usageGrandTotals.totalAmt.toLocaleString('en-IN', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>

                        {/* Pagination Footer */}
                        {filteredUsage.length > usagePerPage && (
                            <div className="row align-center mt-3 mb-2">
                                <div className="col-sm-12 col-md-6">
                                    <div className="dataTables_info" role="status" aria-live="polite">
                                        Showing {Math.min(usagePerPage, filteredUsage.length)} of {filteredUsage.length} projects
                                    </div>
                                </div>
                                <div className="col-sm-12 col-md-6">
                                    <div className="dataTables_paginate paging_simple_numbers">
                                        <Pagination
                                            total={filteredUsage.length}
                                            itemsPerPage={usagePerPage}
                                            currentPage={usagePage}
                                            onPageChange={(page) => setUsagePage(page)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectUsageSummary;
